import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import '../../styles/dash/WeeklyExerciseView.css';
import { tryOpenAppThenStore, APP_STORE_URL, PLAY_STORE_URL } from '../../utils/appStoreLinks';

export default function WeeklyExerciseView() {
    const { uuid } = useParams();
    const [exercise, setExercise] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchExercise();
    }, [uuid]);

    const fetchExercise = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/weekly-exercises/${uuid}`,
                { withCredentials: true }
            );
            setExercise(response.data);
        } catch (error) {
            setError('Kunne ikke hente øvelsesdetaljer');
            console.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    const getContentName = () => {
        if (!exercise) return null;
        return exercise.activityName || exercise.name || 'Ukens øvelse';
    };

    // Actual activity id from backend (weekExercise.activity_idactivity) for app deeplink
    const activityId = exercise?.activity_idactivity ?? exercise?.idactivity;

    if (loading) return <div className="loading">Laster...</div>;
    if (error) return <div className="error-message">{error}</div>;
    if (!exercise) return <div className="not-found">Øvelse ikke funnet</div>;

    const exerciseName = getContentName();
    const subtitle = exercise.activityShortDescription || null;
    const minutes = exercise.minutes != null ? exercise.minutes : null;

    return (
        <div className="weekly-exercise-view">
            <div className="exercise-hero">
                <span className="week-badge">Uke {exercise.week}, {exercise.year}</span>
                <h1 className="exercise-title">{exerciseName}</h1>
                {subtitle && <p className="exercise-subtitle">{subtitle}</p>}
                {minutes != null && (
                    <div className="exercise-meta">
                        <span className="meta-pill meta-minutes">{minutes} min</span>
                    </div>
                )}
            </div>

            <div className="exercise-content">
                {(exercise.activityDescription || exercise.description) && (
                    <div className="description">
                        <h3>Beskrivelse</h3>
                        <p>{exercise.activityDescription || exercise.description}</p>
                    </div>
                )}
                <div className="program-info">
                    <div className="info-grid">
                        {exercise.level_name && (
                            <div className="info-item">
                                <span className="label">Nivå</span>
                                <span className="value">{exercise.level_name}</span>
                            </div>
                        )}
                        {exercise.step_name && (
                            <div className="info-item">
                                <span className="label">Steg</span>
                                <span className="value">{exercise.step_name}</span>
                            </div>
                        )}
                        {exercise.environment_name && (
                            <div className="info-item">
                                <span className="label">Miljø</span>
                                <span className="value">{exercise.environment_name}</span>
                            </div>
                        )}
                        {minutes != null && (
                            <div className="info-item">
                                <span className="label">Varighet</span>
                                <span className="value">{minutes} minutter</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="exercise-footer">
                <p className="cta-teaser">Klar for økten? Åpne appen og start nå.</p>
                <a 
                    href={activityId != null ? `lopeglad://weekly-exercise/${activityId}` : `lopeglad://weekly-exercise/${uuid}`}
                    className="cta-app-button"
                    onClick={(e) => {
                        e.preventDefault();
                        const id = activityId != null ? activityId : uuid;
                        tryOpenAppThenStore(`lopeglad://weekly-exercise/${id}`);
                    }}
                >
                    <span className="cta-app-button-icon">▶</span>
                    Åpne i appen
                </a>
                <p className="store-links-hint">
                    Har du ikke appen installert? Last ned på{' '}
                    <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">App Store</a>
                    {' '}eller{' '}
                    <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">Google Play</a>.
                </p>
            </div>
        </div>
    );
} 